import { Controller } from "@hotwired/stimulus"

// Best practice: cap polls at ~10 min (60 × 10s) to avoid unbounded memory/network use.
// Document generation typically completes in 1–5 min.
const POLL_INTERVAL_MS = 10000
const MAX_POLL_COUNT = 60

// registerInterval(intervalId) is called so the controller can clear the interval on disconnect (e.g. Turbo navigation).
const pollData = (document_obj, registerInterval) => {
  const headers = { 'Accept': 'text/html' }
  const objectId = document_obj.getAttribute('data-object-id')
  const container = document.querySelector(`#document-list-container-${objectId}`)
  if (!container) return

  let pollCount = 0
  const intervalId = setInterval(() => {
    pollCount += 1
    if (pollCount > MAX_POLL_COUNT) {
      clearInterval(intervalId)
      return
    }

    fetch(container.getAttribute('data-url'), { headers })
      .then((response) => response.text())
      .then((body) => {
        container.innerHTML = body
        const doc_obj = container.querySelector("div.document-processing")
        if (doc_obj === null) {
          clearInterval(intervalId)
        }
      })
  }, POLL_INTERVAL_MS)
  registerInterval?.(intervalId)
}

// Start polling for any .document-processing nodes under the given root (e.g. controller element).
// startedIds is mutated so we don't start duplicate polls for the same container.
// intervalIds: optional Set to register interval IDs so the controller can clear them on disconnect (e.g. Turbo navigation).
function startPollingForNodes(root, startedIds, intervalIds) {
  root.querySelectorAll("div.document-processing").forEach((el) => {
    const id = el.getAttribute("data-object-id")
    if (id && !startedIds.has(id)) {
      startedIds.add(id)
      pollData(el, (intervalId) => intervalIds?.add(intervalId))
    }
  })
}

export default class extends Controller {
  connect() {
    this._startedPollIds = new Set()
    this._intervalIds = new Set()
    // Poll on connect if this container already has generating state (e.g. page load with PDF in progress).
    startPollingForNodes(this.element, this._startedPollIds, this._intervalIds)

    // When content is replaced (e.g. turbo_stream after "Download your Plan"),
    // document-processing may appear later; start polling then.
    this._observer = new MutationObserver(() => {
      startPollingForNodes(this.element, this._startedPollIds, this._intervalIds)
    })
    this._observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    this._observer?.disconnect()
    this._intervalIds?.forEach((id) => clearInterval(id))
    this._intervalIds?.clear()
  }
}
